import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Topbar from "@/components/Topbar";
import { HardHat, IdentificationCard, Briefcase, ClockCounterClockwise, CheckCircle, WarningCircle } from "@phosphor-icons/react";

/* v2.88 · Professional Engineer landing dashboard.
   Minimal P0 shell — real profile editor + job board arrive in P1. */
export default function PEDashboard() {
  const { user } = useAuth();
  const status = user?.pe_status || "draft";
  const statusMeta = {
    draft: { color: "text-[#FFCC00]", icon: WarningCircle, label: "DRAFT — complete your profile to submit for review" },
    pending: { color: "text-[#00E5FF]", icon: ClockCounterClockwise, label: "PENDING — admin reviewing your submission" },
    approved: { color: "text-emerald-400", icon: CheckCircle, label: "LIVE — visible in the public directory" },
    pending_revision: { color: "text-[#FFCC00]", icon: ClockCounterClockwise, label: "REVISION PENDING — previous version remains live" },
  }[status] || { color: "text-gray-400", icon: WarningCircle, label: status };
  const StatusIcon = statusMeta.icon;

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="dashboard" />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag">// professional engineer</div>
        <h1 className="font-display text-4xl md:text-5xl font-black mt-1">
          Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
        </h1>
        <p className="text-gray-400 mt-2">Build your public profile — admin will vet it before it goes live in the directory.</p>

        <div className={`mt-6 border ${status === "approved" ? "border-emerald-400/30 bg-emerald-400/5" : "border-[#FFCC00]/30 bg-[#FFCC00]/5"} p-4 flex items-center gap-3`}
             data-testid="pe-status-banner">
          <StatusIcon size={22} weight="duotone" className={statusMeta.color} />
          <div className={`text-sm font-mono ${statusMeta.color}`}>{statusMeta.label}</div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link to="/pe/profile" className="border border-white/10 hover:border-[#00E5FF]/60 p-6 bg-[#12151C] transition group"
                data-testid="pe-tile-profile">
            <IdentificationCard size={28} weight="duotone" className="text-[#00E5FF]" />
            <div className="label-tag mt-3">// step 1 · profile</div>
            <h3 className="font-display text-2xl font-bold mt-1 group-hover:text-[#00E5FF] transition">Your PE profile</h3>
            <p className="text-sm text-gray-400 mt-2">
              Country, competencies, certifications, portfolio, and templates. Submit for admin review.
            </p>
            <div className="mt-4 text-xs font-mono text-[#00E5FF]">Open profile →</div>
          </Link>
          <Link to="/jobs" className="border border-white/10 hover:border-[#00E5FF]/60 p-6 bg-[#12151C] transition group"
                data-testid="pe-tile-jobs">
            <Briefcase size={28} weight="duotone" className="text-[#00E5FF]" />
            <div className="label-tag mt-3">// step 2 · respond</div>
            <h3 className="font-display text-2xl font-bold mt-1 group-hover:text-[#00E5FF] transition">Browse open jobs</h3>
            <p className="text-sm text-gray-400 mt-2">
              Submit RFP-style proposals with itemized scope, milestones, and fees. Message clients in-app.
            </p>
            <div className="mt-4 text-xs font-mono text-[#00E5FF]">Open job board →</div>
          </Link>
        </div>

        <div className="mt-10 border border-white/10 bg-white/5 p-4 text-xs font-mono text-gray-400 flex items-center gap-2"
             data-testid="pe-info-banner">
          <HardHat size={16} className="text-gray-500" />
          You&apos;re in early access. Full profile editor + job board unlock in the next release.
        </div>
      </div>
    </div>
  );
}
